// ============================================================
// TMR Portal Admin — the access approval queue (10 B7d)
// Reached only with authorization.admin — see nav.ts/Portal.
// ============================================================
import { useEffect, useState } from 'react';
import { pageCount } from '@tmr/api-client';
import { Btn, StatusBadge } from '../../components/primitives';
import { PageHeader, FilterTabs, Table, Td } from '../../components/ui';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyableText } from '../../components/CopyableText';
import { Pagination } from '../../components/Pagination';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useCapabilities } from '../../data/PermissionsProvider';
import { accessRequestStatusLabel, canReissueInvite, inviteSituation } from '../../data/authorization/labels';
import {
  useAccessRequestInviteLink,
  useAccessRequests,
  useAuthorizationRoles,
  useRejectAccessRequest,
  useReissueAccessRequestInvite,
  useSyncAccessRequests,
} from '../../data/authorization/queries';
import type { AccessRequestListItem, AccessRequestStatus } from '../../data/authorization/contract';
import { ApproveAccessModal } from './ApproveAccessModal';
import { InviteLinkDialog } from './InviteLinkDialog';
import { ErrorBanner } from './ErrorBanner';

// A aba filtra a DECISÃO no servidor, que é o que o backend indexa. "Convidadas" e não "Aprovadas"
// porque é o conjunto inteiro de quem foi aprovado — de quem ainda não abriu o e-mail a quem já entrou.
// A coluna Situação é que diz em qual desses pontos cada pessoa está.
const TABS: { value: AccessRequestStatus; label: string }[] = [
  { value: 'Pending', label: 'Aguardando' },
  { value: 'Approved', label: 'Convidadas' },
  { value: 'Rejected', label: 'Rejeitadas' },
];

/**
 * Who asked for access to the platform, and what was decided.
 *
 * The queue lives in the TMR database, but deciding one reaches the Clerk Admin API — so the screen has
 * a state for an environment wired without it, told by `capabilities.accessRequests` rather than guessed
 * from a status code. Approving opens {@link ApproveAccessModal}, which is where the roles are chosen;
 * rejecting is a single confirmation, because there is nothing to compose.
 */
export function AccessRequestsPage() {
  const [status, setStatus] = useState<AccessRequestStatus>('Pending');
  const [page, setPage] = useState(1);

  // The environment says whether this surface exists; the screen no longer guesses it from a 404. The
  // three access-request routes are only mapped when the backend holds a Clerk secret key, and the flag
  // is read from that same configuration — while a bare 404 is equally what a bad ingress rule, a wrong
  // base path or a half-finished deploy produce, and telling the on-call it is a Clerk setting sends
  // them somewhere else entirely.
  const clerkOff = !useCapabilities().accessRequests;

  const requests = useAccessRequests({ status, page }, !clerkOff);
  const roles = useAuthorizationRoles();
  const sync = useSyncAccessRequests();
  const reject = useRejectAccessRequest();
  const [approving, setApproving] = useState<AccessRequestListItem | null>(null);
  const [toReject, setToReject] = useState<AccessRequestListItem | null>(null);

  // The manual fallback for an invitation whose e-mail never lands (06 §6.1). Held in a mutation rather
  // than a query on purpose — the link is a credential, and it should be fetched when somebody decides to
  // send it, not cached under a key and refetched on a window focus.
  const [showingInvite, setShowingInvite] = useState<AccessRequestListItem | null>(null);
  const inviteLink = useAccessRequestInviteLink();

  // "Reenviar convite" (06 §6.1): the way out for an invitation that expired unread, never arrived, or was
  // given up on. Kept with the name it was for, so the result banner can say whose invitation went out.
  const reissue = useReissueAccessRequestInvite();
  const [toReissue, setToReissue] = useState<AccessRequestListItem | null>(null);
  const [reissuedName, setReissuedName] = useState<string | null>(null);

  // Deciding the last row of the last page shrinks the list under the operator's feet: the page they are
  // on stops existing, and the empty state renders without the pagination control that would bring them
  // back. Following the list down is the fix — the alternative is a screen with no way out but a reload.
  // Never below 1: an emptied list has a page count of zero, and page 0 is not a page the backend has.
  const pages = Math.max(1, requests.data ? pageCount(requests.data) : 1);
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  // A filter change keeps the previous page's rows on screen while the next one loads (`placeholderData`
  // — the alternative is the table blanking on every tab click). They are the *old* filter's rows, so the
  // screen has to say so: pending rows under "Rejeitadas", each with a live Aprovar button, is a queue
  // lying about what it holds.
  const stale = requests.isPlaceholderData && requests.isFetching;

  // A mutation banner belongs to the list it fired on. Moving to another tab or page makes it a claim
  // about rows that are no longer there, so the result is dropped along with the view it described.
  useEffect(() => {
    sync.reset();
    reject.reset();
    reissue.reset();
    // Only the view identity matters here; the mutations are stable for the life of the screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Autorização"
        title="Acessos"
        desc="Quem pediu acesso à plataforma. Aprovar convida a pessoa e concede os papéis escolhidos."
        actions={
          <Btn variant="secondary" icon={Icons.swap} onClick={() => sync.mutate()} disabled={sync.isPending || clerkOff}>
            {sync.isPending ? 'Sincronizando…' : 'Sincronizar'}
          </Btn>
        }
      />

      {sync.isSuccess && (
        <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
          {sync.data.created === 0
            ? 'Nenhuma solicitação nova na lista de espera.'
            : `${sync.data.created} solicitação(ões) trazida(s) da lista de espera.`}
        </div>
      )}
      {sync.isError && <ErrorBanner error={sync.error} />}
      {reject.isError && <ErrorBanner error={reject.error} />}
      {reissue.isError && <ErrorBanner error={reissue.error} />}
      {reissue.isSuccess && reissuedName && (
        <ReissueOutcome name={reissuedName} sent={reissue.data.inviteSent} />
      )}

      <FilterTabs
        tabs={TABS}
        value={status}
        onChange={(value) => {
          setStatus(value as AccessRequestStatus);
          setPage(1);
        }}
      />

      {clerkOff ? (
        <ClerkNotConfigured />
      ) : requests.isPending ? (
        <Skeleton rows={6} />
      ) : requests.isError ? (
        <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
      ) : requests.data.items.length === 0 ? (
        <>
          <EmptyState
            message={`Nenhuma solicitação ${accessRequestStatusLabel(status).toLowerCase()}.`}
            hint={status === 'Pending' ? 'Sincronize para trazer a lista de espera.' : undefined}
          />
          {/* Rendered outside the non-empty branch on purpose: an empty page is exactly when the
              operator needs the control that takes them back to a page that is not. */}
          <Pagination
            page={requests.data.page}
            pageCount={pageCount(requests.data)}
            total={requests.data.totalCount}
            onChange={setPage}
          />
        </>
      ) : (
        <div aria-busy={stale} style={{ opacity: stale ? 0.55 : 1, transition: 'opacity 120ms var(--ease-out)' }}>
          <Table
            columns={[
              { label: 'Pessoa' },
              { label: 'Solicitada em', width: 160 },
              { label: 'Situação', width: 130 },
              { label: '', align: 'right', width: 300 },
            ]}
          >
            {requests.data.items.map((request) => (
              <tr key={request.id} className="pa-row">
                <Td>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{request.name}</div>
                  <CopyableText value={request.email} style={{ fontSize: 12, color: 'var(--fg-muted)' }} />
                </Td>
                <Td>
                  <span style={{ fontSize: 13 }}>{formatInstant(request.requestedAt)}</span>
                </Td>
                <Td>
                  {/* A decisão sozinha diria "Aprovada" para quem sequer abriu o e-mail. O que o
                      operador precisa ver é onde a pessoa está: Enviado → Criado. */}
                  <StatusBadge status={inviteSituation(request.status, request.inviteDelivery)} />
                </Td>
                <Td align="right">
                  {request.status === 'Pending' ? (
                    // Disabled while the rows are the previous filter's: deciding one of those would be
                    // deciding a request the operator is not looking at any more.
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Btn variant="secondary" size="sm" disabled={stale} onClick={() => setToReject(request)}>Rejeitar</Btn>
                      <Btn variant="primary" size="sm" icon={Icons.check} disabled={stale} onClick={() => setApproving(request)}>
                        Aprovar
                      </Btn>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{lastEventOf(request)}</span>
                      {/* Só onde há convite vivo para entregar: uma solicitação rejeitada não tem um, e
                          quem já concluiu o cadastro tem um link gasto. Oferecer nesses dois casos seria
                          prometer uma saída que o backend recusa. */}
                      {canHandDeliver(request) && (
                        <Btn
                          variant="secondary"
                          size="sm"
                          icon={Icons.copy}
                          disabled={stale || inviteLink.isPending}
                          onClick={() => {
                            setShowingInvite(request);
                            inviteLink.mutate(request.id);
                          }}
                        >
                          Convite
                        </Btn>
                      )}
                      {canReissueInvite(request.status, request.inviteDelivery) && (
                        <Btn
                          variant="secondary"
                          size="sm"
                          icon={Icons.send}
                          disabled={stale || reissue.isPending}
                          onClick={() => setToReissue(request)}
                        >
                          Reenviar
                        </Btn>
                      )}
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
          <Pagination
            page={requests.data.page}
            pageCount={pageCount(requests.data)}
            total={requests.data.totalCount}
            onChange={setPage}
          />
        </div>
      )}

      {approving && (
        <ApproveAccessModal
          request={approving}
          roles={roles.data ?? []}
          onClose={() => setApproving(null)}
        />
      )}
      {showingInvite && (
        <InviteLinkDialog
          personName={showingInvite.name}
          link={inviteLink.data}
          loading={inviteLink.isPending}
          error={inviteLink.isError ? inviteLink.error : undefined}
          onClose={() => {
            setShowingInvite(null);
            // Dropped with the dialog: a live invitation link has no reason to outlive the moment an
            // operator was looking at it.
            inviteLink.reset();
          }}
        />
      )}
      {toReject && (
        <ConfirmDialog
          title="Rejeitar solicitação"
          danger
          message={
            <>
              Rejeitar o pedido de <strong>{toReject.name}</strong>? A pessoa sai da lista de espera e não
              recebe convite.
            </>
          }
          confirmLabel="Rejeitar"
          onConfirm={() => reject.mutate(toReject.id)}
          onClose={() => setToReject(null)}
        />
      )}
      {toReissue && (
        <ConfirmDialog
          title="Reenviar convite"
          icon={Icons.send}
          message={
            <>
              Enviar um convite novo para <strong>{toReissue.name}</strong>? O link do convite anterior deixa
              de funcionar.
            </>
          }
          confirmLabel="Reenviar"
          // Waits for the answer: closing first would let a second click send a second invitation. A
          // failure is not rethrown — it closes the dialog and shows in the banner, like the other actions.
          onConfirm={() => {
            setReissuedName(toReissue.name);
            return reissue.mutateAsync(toReissue.id).catch(() => undefined);
          }}
          onClose={() => setToReissue(null)}
        />
      )}
    </div>
  );
}

/**
 * The result of "Reenviar convite", in the operator's words.
 *
 * The old invitation is gone either way, so a queued new one is not a failure — but nobody has been
 * e-mailed yet, and saying "reenviado" there would send the operator off believing the person is sorted.
 * The backend's reason is left out on purpose: it names internal services (skill `frontend-conventions`,
 * Regra 3), and the operator's next step is the same whatever it says.
 */
function ReissueOutcome({ name, sent }: { name: string; sent: boolean }) {
  return (
    <div role="status" style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
      {sent ? (
        <>Convite reenviado para <strong>{name}</strong>. O link anterior não vale mais.</>
      ) : (
        <>
          O convite anterior de <strong>{name}</strong> foi cancelado, mas o novo <strong>ainda não saiu</strong>.
          O envio será repetido automaticamente.
        </>
      )}
    </div>
  );
}

/**
 * What the screen says when the backend has no Clerk secret key.
 *
 * The queue itself lives in the TMR database, but approving means inviting through the Clerk Admin API
 * (06 §6.1) — so without the credential the whole surface is unmapped. Saying that is more useful than
 * a retry button for a route that will not appear.
 */
function ClerkNotConfigured() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '38px 24px', color: 'var(--stone-400)' }} role="status">
      <StrokeIcon d={Icons.shield} size={22} />
      <p style={{ margin: '10px 0 0', fontFamily: 'var(--font-sans)', fontSize: 14.5, color: 'var(--ink-900)' }}>
        A aprovação de acessos não está disponível neste ambiente.
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--fg-subtle)', maxWidth: 420 }}>
        Fale com o time de tecnologia para habilitá-la.
      </p>
    </div>
  );
}

/**
 * Whether there is a live invitation an operator could deliver by hand.
 *
 * Approved and not yet accepted — the window in which somebody is waiting for an e-mail that may never
 * arrive. `Abandoned` counts: the reconciler gave up trying, which is precisely when a human takes over.
 */
function canHandDeliver(request: AccessRequestListItem): boolean {
  return request.status === 'Approved' && request.inviteDelivery !== 'Accepted';
}

/**
 * The last thing that happened to a decided request, in one line.
 *
 * Reads the invitation's own timestamps before the decision's: on an approved row the decision is the
 * least interesting date on it — it is always the oldest, and it says nothing about whether the person
 * ever arrived.
 */
function lastEventOf(request: AccessRequestListItem): string {
  if (request.inviteAcceptedAt) return `acesso criado em ${formatInstant(request.inviteAcceptedAt)}`;
  if (request.inviteSentAt) return `convite enviado em ${formatInstant(request.inviteSentAt)}`;
  return request.decidedAt ? `decidida em ${formatInstant(request.decidedAt)}` : 'decidida';
}

/** An ISO instant as the operator reads it, in their own time zone. */
function formatInstant(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
